import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { messages } from './cash-flow-modal-messages';
import { useClickAwayListener } from '@algoan/dashboard/utils';
import { TransactionCategory, AutocompleteInputType } from '@algoan/dashboard/model';
import { ArrowIcon } from '../icons';
import { useTransactionCategoryTranslator } from '../../messages';
import { CategoryDropdownOption } from './categories-list';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { ListItemAutocomplete } from './list-item-autocomplete';
import { ListItemSelect } from './list-item-select';

interface ModalDropDownProps {
  categoriesList: CategoryDropdownOption[];
  dropDownHeader: string;
  isDisabled?: boolean;
  selectedCategory: TransactionCategory;
  setSelectedCategory: (newCategory: TransactionCategory) => void;
  oldCategory?: TransactionCategory;
  currentCategory?: TransactionCategory;
  className?: string;
}

export const ModalDropdown: React.FC<ModalDropDownProps> = ({
  categoriesList,
  dropDownHeader,
  isDisabled,
  selectedCategory,
  setSelectedCategory,
  oldCategory,
  currentCategory,
  className,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const [switchInput, setSwitchInput] = useState(AutocompleteInputType.SELECT);
  const [openMenu, setOpenMenu] = useState(false);
  const translateTransactionCategory = useTransactionCategoryTranslator();
  const [textFieldValue, setTextFieldValue] = useState(translateTransactionCategory(selectedCategory));

  const toggleDropdown = (): void => {
    if (!isDisabled) {
      setOpenMenu(!openMenu);
    }
  };

  const dropdownContainerRef = useClickAwayListener<HTMLDivElement>(() => {
    setOpenMenu(false);
    setSwitchInput(AutocompleteInputType.SELECT);
    setTextFieldValue(translateTransactionCategory(selectedCategory));
  });

  const handleChangeCategory = (newSelectedCategory: TransactionCategory): void => {
    setSelectedCategory(newSelectedCategory);
    logAmplitudeEvent(AmplitudeEvent.CASHFLOW_CHANGE, {
      selectedCategory,
      newSelectedCategory,
    });
    setTextFieldValue(translateTransactionCategory(newSelectedCategory));
    setSwitchInput(AutocompleteInputType.SELECT);
    setOpenMenu(false);
  };

  const handleChangeTextFieldValue = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (switchInput === AutocompleteInputType.SELECT) {
      setTextFieldValue(
        event.target.value.length > textFieldValue?.length ? event.target.value[event.target.value.length - 1] : '',
      );
      setSwitchInput(AutocompleteInputType.AUTOCOMPLETE);
    } else {
      if (switchInput === AutocompleteInputType.AUTOCOMPLETE) setTextFieldValue(event.target.value);
    }
  };

  return (
    <S.DropdownContainer
      data-testid="recategorization-dropdown"
      ref={dropdownContainerRef}
      onClick={() => toggleDropdown()}
      className={className}
    >
      <S.Dropdown openMenu={openMenu} isDisabled={!!isDisabled}>
        {openMenu ? (
          <S.DropdownInput
            autoFocus
            placeholder={intl.formatMessage(messages.placeholderInput)}
            onChange={handleChangeTextFieldValue}
            value={textFieldValue ? textFieldValue : ''}
          />
        ) : (
          <S.DisplayCategory isDisabled={!!isDisabled}>
            {translateTransactionCategory(selectedCategory)}
          </S.DisplayCategory>
        )}
        {!isDisabled && (
          <ArrowIcon
            color={openMenu ? theme.secondary.softBlue3 : theme.secondary.softBlue1}
            direction={openMenu ? 'up' : 'down'}
            size="large"
          />
        )}
      </S.Dropdown>

      {openMenu && (
        <S.DropdownListContainer>
          {textFieldValue?.length > 1 && switchInput === AutocompleteInputType.AUTOCOMPLETE ? (
            <ListItemAutocomplete
              textFieldValue={textFieldValue}
              handleChangeCategory={handleChangeCategory}
              categoriesList={categoriesList}
              dropDownHeader={dropDownHeader}
              oldCategory={oldCategory}
              currentCategory={currentCategory}
            />
          ) : (
            <ListItemSelect
              categoriesList={categoriesList}
              handleChangeCategory={handleChangeCategory}
              dropDownHeader={dropDownHeader}
              oldCategory={oldCategory}
              currentCategory={currentCategory}
            />
          )}
        </S.DropdownListContainer>
      )}
    </S.DropdownContainer>
  );
};

const S = {
  DropdownContainer: styled.div`
    position: relative;
    height: 45px;
    width: 350px;
    margin-left: 50px;
  `,
  Dropdown: styled.div<{ openMenu: boolean; isDisabled: boolean }>`
    align-items: center;
    border-radius: 8px;
    border: 1px solid ${({ openMenu, theme }) => (openMenu ? theme.secondary.softBlue3 : theme.secondary.softBlue1)};
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    height: 45px;
    justify-content: space-between;
    padding: 12px 16px;
    width: 350px;

    ${({ isDisabled, theme }) =>
      isDisabled
        ? `
      background-color: ${theme.secondary.chalk};
      text-align: center;
      border-color: ${theme.secondary.chalk};
      justify-content: center;
        `
        : `
      &:hover {
        cursor: pointer;
        border: 1px solid ${theme.secondary.softBlue3};

        & path {
          stroke: ${theme.secondary.softBlue3};
        }
      }
    `}
  `,
  DisplayCategory: styled.div<{ isDisabled?: boolean }>`
    font-size: 15px;
    font-weight: bold;
    line-height: 19px;
    color: ${(props) => props.theme.algoanBlue};
    width: ${(props) => (props.isDisabled ? '100%' : '290px')};
    height: 19px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  DropdownInput: styled.input`
    padding-left: 0;
    font-size: 15px;
    font-weight: bold;
    line-height: 19px;
    color: ${(props) => props.theme.algoanBlue};
    width: 290px;
    height: 19px;
    border: none;
    outline: none;

    &::placeholder {
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 19px;
      color: ${(props) => props.theme.secondary.softBlue1};
    }
  `,
  DropdownListContainer: styled.div`
    position: relative;
    z-index: 1;
  `,
};
